import { apiClient } from "@/lib/api/client";
import {
  invalidateStaleWorkspaceOnMismatch,
  isWorkspaceOrgMismatchError,
} from "@/lib/workspaceOrgMismatch";

export type OnboardingStatus = {
  completed: boolean;
  mustOnboard: boolean;
  skipped: boolean;
  workspaceCount: number;
  currentStep: string;
  completedSteps: string[];
  completedAt: string | null;
  skippedAt: string | null;
  /** Legacy field — may be absent on newer backends. */
  onboardingStatus?: string;
};

/**
 * Derived onboarding status as a single string union for route policies.
 *
 * The backend intentionally returns composable flags; route policies need a
 * stable single value that preserves the older policy semantics.
 */
export type OnboardingStatusValue =
  | "not_started"
  | "in_progress"
  | "completed"
  | "dismissed";

/**
 * Derive a single OnboardingStatusValue from a **confirmed** OnboardingStatus payload.
 *
 * Precedence: completed > dismissed > not_started > in_progress.
 *
 * Returns `undefined` when status is missing — a fetch failure / unknown state must
 * NOT be asserted as `"not_started"` (that falsely re-opens the onboarding wizard).
 */
export function deriveOnboardingStatusValue(
  status: OnboardingStatus | undefined | null,
): OnboardingStatusValue | undefined {
  if (!status) return undefined;
  if (status.completed) return "completed";
  if (status.skipped) return "dismissed";
  if (status.mustOnboard) return "not_started";
  return "in_progress";
}

/**
 * Unwrap the backend envelope: { data: {...}, meta: {...} }
 * Falls back to raw response if no envelope.
 */
function unwrap<T>(response: any): T {
  return response?.data ?? response;
}

async function fetchOnboardingStatusOnce(): Promise<OnboardingStatus> {
  // apiClient.get already unwraps one `{ data: T }` layer — do not destructure `.data` again.
  const payload = await apiClient.get<unknown>("/organizations/onboarding/status");
  const status = unwrap<OnboardingStatus>(payload);
  // Backend returns { completed, mustOnboard, skipped, workspaceCount, ... }
  // Validate against the actual contract — must have `mustOnboard` boolean.
  if (!status || typeof status !== "object" || typeof (status as any).mustOnboard !== "boolean") {
    throw new Error("Invalid onboarding status response");
  }
  return status;
}

export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  try {
    return await fetchOnboardingStatusOnce();
  } catch (err) {
    // Stale x-workspace-id → clear cache and retry once without the bad header.
    if (isWorkspaceOrgMismatchError(err)) {
      invalidateStaleWorkspaceOnMismatch(err);
      return await fetchOnboardingStatusOnce();
    }
    throw err;
  }
}

export async function skipOnboarding(): Promise<{ success: boolean; message?: string }> {
  const payload = await apiClient.post("/organizations/onboarding/skip");
  return unwrap(payload) ?? { success: false };
}

export async function completeOnboarding(): Promise<{ success: boolean; message?: string }> {
  const payload = await apiClient.post("/organizations/onboarding/complete");
  return unwrap(payload) ?? { success: false };
}

/** Wizard steps the server accepts. Mirrors the backend CompleteOnboardingStepDto allow-list. */
export type OnboardingStepId = "org" | "workspace" | "starter" | "invite";

/**
 * Mark one wizard step complete.
 *
 * Feeds `organizations.settings.onboarding.completedSteps`, which is the ONLY
 * signal `deriveSetupChecklist` has for the "Invite teammates" row — the other
 * two rows fall back to `workspaceCount > 0`. Until ONBOARDING-SKIP-AND-
 * BREADCRUMB-1 this endpoint had zero callers, so that row could never complete.
 *
 * Call ONLY after a step genuinely succeeded; never on skip.
 */
export async function completeOnboardingStep(
  step: OnboardingStepId,
): Promise<{ success: boolean; step?: string; completedSteps?: string[] }> {
  const payload = await apiClient.post("/organizations/onboarding/complete-step", { step });
  return unwrap(payload) ?? { success: false };
}
